import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos sin actividad
const SESSION_DATE_KEY = 'tk_session_date';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const timerRef = useRef(null);

  const logout = useCallback(async () => {
    localStorage.removeItem(SESSION_DATE_KEY);
    await supabase.auth.signOut();
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const storedDate = localStorage.getItem(SESSION_DATE_KEY);
        const today = new Date().toDateString();
        if (storedDate && storedDate !== today) {
          logout();
          return; //
        }
        localStorage.setItem(SESSION_DATE_KEY, today);
      }
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        // Solo escribir la fecha si no existe: distingue un login nuevo (key vacía)
        // de una sesión restaurada (key con la fecha original intacta para el chequeo).
        // Si sobreescribiéramos siempre, la race condition haría que getSession()
        // vea siempre "hoy" y nunca cierre la sesión del día anterior.
        if (!localStorage.getItem(SESSION_DATE_KEY)) {
          localStorage.setItem(SESSION_DATE_KEY, new Date().toDateString());
        }
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem(SESSION_DATE_KEY);
      }
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [logout]);

  // Timer de inactividad: se resetea con cualquier interacción del usuario
  useEffect(() => {
    if (!user) return;

    const reset = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  // app_metadata solo es editable por service_role (no por el usuario mismo)
  const isAdmin = user?.app_metadata?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
